"use client";

import React, { useState } from "react";
import { env } from "@/env";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import LoadingSpinner from "@/components/ui/loading-spinner";

interface CreateSlugResponse {
  message: string;
  id: string;
  slug: string;
  url: string;
  errors?: Record<string, any>;
}

const CreateSocialPage = () => {
  const { accessToken } = useAuth();
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    target_link: "",
    video_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [createdUrl, setCreatedUrl] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setCreatedUrl("");

    // client-side basic validation for video URL
    if (formData.video_url) {
      try {
        const u = new URL(formData.video_url);
        if (
          !u.hostname.includes("youtube.com") &&
          !u.hostname.includes("youtu.be")
        ) {
          throw new Error("Video URL must be a YouTube link");
        }
      } catch (err) {
        setMessage(`Error: ${(err as Error).message}`);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/social/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      const data: CreateSlugResponse = await response.json();

      console.log(data);

      if (response.ok) {
        setMessage("Social link created successfully");
        setCreatedUrl(data.url);
        setFormData({ slug: "", title: "", target_link: "", video_url: "" });
      } else {
        let errorMessage = "Failed to create social link";

        if (data.errors) {
          const validationErrors = Object.entries(data.errors).map(
            ([field, error]: [string, any]) => {
              if (error.param?.msg) {
                return `${field}: ${error.param.msg}`;
              }
              return `${field}: Invalid value`;
            },
          );
          errorMessage = validationErrors.join(", ");
        } else if (data.message) {
          errorMessage = data.message;
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      setMessage(`Error: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(createdUrl);
    setMessage("URL copied to clipboard!");
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="container mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-primary/5 bg-white/5 p-8 shadow-lg">
          <h1 className="mb-2 text-3xl font-medium">Create Social Link</h1>
          <p className="mb-6 text-sm tracking-tight text-muted-foreground">
            Create a new social verification link. Users will complete 3 steps
            before being redirected to the target URL.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <Label htmlFor="slug">Slug*</Label>
              <Input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                placeholder="e.g., premium-access"
                required
                pattern="[a-z0-9-]+"
              />
              <small className="text-xs text-gray-500">
                URL-friendly identifier (lowercase letters, numbers, and hyphens
                only)
              </small>
            </div>

            <div className="form-group">
              <Label htmlFor="title">Title*</Label>
              <Input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Premium Script Access"
                required
              />
              <small className="text-xs text-gray-500">
                This will be displayed as the page title
              </small>
            </div>

            <div className="form-group">
              <Label htmlFor="target_link">Target Link*</Label>
              <Input
                type="url"
                id="target_link"
                name="target_link"
                value={formData.target_link}
                onChange={handleInputChange}
                placeholder="https://example.com/final-destination"
                required
              />
              <small className="text-xs text-gray-500">
                Users will be redirected here after completing all steps
              </small>
            </div>

            <div className="form-group">
              <Label htmlFor="video_url">YouTube Video URL (optional)</Label>
              <Input
                type="url"
                id="video_url"
                name="video_url"
                value={formData.video_url}
                onChange={handleInputChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <small className="text-xs text-gray-500">
                Visitors will be asked to watch this video for 15 seconds before
                progressing
              </small>
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <span className="flex items-center justify-center">
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Creating...
                </span>
              ) : (
                "Create Social Link"
              )}
            </Button>
          </form>

          {message && (
            <Alert
              variant={message.startsWith("Error") ? "destructive" : "default"}
              className="mt-6"
            >
              {message}
            </Alert>
          )}

          {createdUrl && (
            <div className="mt-6 flex flex-row items-center space-x-2">
              <Input type="text" value={createdUrl} readOnly />
              <Button variant="outline" onClick={copyToClipboard}>
                Copy
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateSocialPage;
